import {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {QueryFunctionContext, useQuery} from 'react-query';
import {SingleValue} from 'react-select';

import ViewDetails from 'components/ViewDetails';

import {ListResponse, SelectOption, User} from 'types';
import api from 'api';
import {getFullName} from 'helpers/user';
import roles, {ROLES_LABELS} from 'constants/roles';
import Switch from 'react-switch';
import localStyles from './Employees.module.scss';
import useSort from 'hooks/useSort';
import useDebounce from 'hooks/useDebounce';
import {phoneFormat} from 'helpers/phoneFormat';
import InfoIcon from '../../EmployeeForm/icons/Info';
import useCompanyConfig from 'hooks/useCompanyConfig';

const filterOptions: SelectOption[] = [
  {value: '', label: 'All Roles'},
  ...roles,
];

async function getEmployees({queryKey}: QueryFunctionContext) {
  const [, role, order, keyWord] = queryKey;
  const {data} = await api.get<ListResponse<User>>('/users/', {
    params: {role, order, search: keyWord ? keyWord : undefined},
  });

  return data;
}

function useEmployees() {
  const {url} = useRouteMatch();
  const history = useHistory();
  const [keyWord, setKeyWord] = useState(
    sessionStorage.getItem(`search_employe`) || ''
  );
  const [filterValue, setFilterValue] = useState<SelectOption>(
    filterOptions[0]
  );
  const {sortBy, sortOrder, order, onSort} = useSort('name');
  const {isPropertyManager, isMSPProvider} = useCompanyConfig();
  const searchDebounced = useDebounce(keyWord, 400);

  const {data: employees, isLoading} = useQuery(
    ['employees', filterValue.value, order, searchDebounced],
    getEmployees
  );

  const onViewMember = useCallback(
    (id: number) => {
      history.push(`${url}/${id}`, {
        isStaff: true,
      });
    },
    [url, history]
  );

  const data = useMemo(
    function () {
      if (!employees) return [];
      return employees.results
        .filter((employee) => employee.role !== 'customer')
        .map((employee) => {
          const role = roles.find((item) => item.value === employee.role);
          const roleLabel =
            isMSPProvider && employee.role === 'landlord'
              ? 'Owner'
              : ROLES_LABELS[employee.role];
          const roleDescription =
            isMSPProvider && employee.role === 'landlord'
              ? 'Company Owner'
              : role?.description;
          return {
            ...employee,
            name: getFullName(employee),
            role: role ? (
              <div className={localStyles.role}>
                {roleLabel}
                <div className={localStyles.info}>
                  <InfoIcon />
                  <div className={localStyles.tooltip}>{roleDescription}</div>
                </div>
              </div>
            ) : (
              ''
            ),
            phone: phoneFormat(employee.phone),
            active:
              employee.role !== 'management' ? (
                <EmployeStatus id={employee.id} active={employee.is_active} />
              ) : null,
            view: (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onViewMember(employee.id);
                }}
              >
                <ViewDetails disabled to={`${url}/${employee.id}`} />
              </div>
            ),
          };
        });
    },
    [employees, onViewMember, url, isMSPProvider]
  );

  function onChangeFilter(newValue: SingleValue<SelectOption>) {
    newValue && setFilterValue(newValue);
  }

  const goToCreate = () => {
    history.push('/people/employees/new', {
      isStaff: true,
    });
  };
  const filterOptionsBasedOnCompanyType = useMemo(() => {
    const propertyManagerOptions = [{value: '', label: 'All Roles'}, ...roles];
    const mspOptions = propertyManagerOptions.map(
      (option: (typeof propertyManagerOptions)[0]) => ({
        ...option,
        label: option.value === 'landlord' ? 'Owner' : option.label,
      })
    );
    const defaultOptions = propertyManagerOptions.filter(
      (item) => item.value !== 'landlord'
    );

    return isPropertyManager
      ? propertyManagerOptions
      : isMSPProvider
      ? mspOptions
      : defaultOptions;
  }, [isPropertyManager, isMSPProvider]);

  return {
    data,
    isLoading,
    filterOptions: filterOptionsBasedOnCompanyType,
    filterValue,
    sortBy,
    sortOrder,
    keyWord,
    setKeyWord,
    onChangeFilter,
    onSort,
    goToCreate,
  };
}

export const EmployeStatus = ({active, id}: {active: boolean; id: number}) => {
  const [checked, setChecked] = useState(active);

  useEffect(() => {
    setChecked(active);
  }, [active]);

  const onChange = () => {
    setChecked((pr) => !pr);
    api
      .patch<User>(`/users/${id}/`, {
        is_active: !checked,
      })
      .then(() => {})
      .catch((err) => {
        setChecked((pr) => !pr);
      });
  };

  return (
    <div
      style={{
        paddingRight: 60,
      }}
    >
      <label>
        <Switch
          onColor="#4000FF"
          onChange={onChange}
          handleDiameter={17}
          height={20}
          width={40}
          checked={checked}
        />
      </label>
    </div>
  );
};

export default useEmployees;
